import { useEffect, useRef, useCallback, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

// HTTP URL for SockJS (not WebSocket URL)
// WebSocket endpoint is at /api/ws (same context-path as REST API)
const getSockJsUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl.startsWith('http')) {
    // API URL is like "http://localhost:8181/api" - append /ws
    const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    return `${baseUrl}/ws`;
  }
  // Fallback: use current host with /api/ws
  return `${window.location.origin}/api/ws`;
};

/**
 * WebSocket Hook for real-time shopping list updates using STOMP over SockJS
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Subscribe/unsubscribe to specific lists
 * - Event callbacks for different update types
 */
export const useShoppingListWebSocket = (options = {}) => {
  const { user } = useAuth();
  const clientRef = useRef(null);
  const subscriptionsRef = useRef(new Map());
  const [isConnected, setIsConnected] = useState(false);

  const {
    onItemToggle,
    onItemAdded,
    onItemRemoved,
    onListUpdate,
    onCollaboratorJoined,
    onCollaboratorLeft
  } = options;

  // Store callbacks in refs to avoid reconnection on callback changes
  const callbacksRef = useRef({
    onItemToggle,
    onItemAdded,
    onItemRemoved,
    onListUpdate,
    onCollaboratorJoined,
    onCollaboratorLeft
  });

  useEffect(() => {
    callbacksRef.current = {
      onItemToggle,
      onItemAdded,
      onItemRemoved,
      onListUpdate,
      onCollaboratorJoined,
      onCollaboratorLeft
    };
  }, [onItemToggle, onItemAdded, onItemRemoved, onListUpdate, onCollaboratorJoined, onCollaboratorLeft]);

  // Handle incoming messages
  const handleMessage = useCallback((message) => {
    try {
      const data = JSON.parse(message.body);
      logger.debug('WebSocket message received:', data.type, data);

      switch (data.type) {
        case 'ITEM_TOGGLE':
          callbacksRef.current.onItemToggle?.(data);
          break;
        case 'ITEM_ADDED':
          callbacksRef.current.onItemAdded?.(data);
          break;
        case 'ITEM_REMOVED':
          callbacksRef.current.onItemRemoved?.(data);
          break;
        case 'LIST_UPDATE':
          callbacksRef.current.onListUpdate?.(data);
          break;
        case 'COLLABORATOR_JOINED':
          callbacksRef.current.onCollaboratorJoined?.(data);
          break;
        case 'COLLABORATOR_LEFT':
          callbacksRef.current.onCollaboratorLeft?.(data);
          break;
        default:
          logger.debug('Unknown WebSocket message type:', data.type);
      }
    } catch (err) {
      logger.error('Error parsing WebSocket message:', err);
    }
  }, []);

  // Subscribe to a list
  const subscribe = useCallback((listId) => {
    const listIdStr = String(listId);

    if (subscriptionsRef.current.has(listIdStr)) {
      return;
    }

    if (!clientRef.current || !isConnected) {
      subscriptionsRef.current.set(listIdStr, null);
      return;
    }

    try {
      const destination = `/topic/shopping-list/${listIdStr}`;
      const subscription = clientRef.current.subscribe(destination, handleMessage);
      subscriptionsRef.current.set(listIdStr, subscription);
      logger.debug('WebSocket subscribed to:', destination);
    } catch (err) {
      logger.error('Error subscribing to list:', listIdStr, err);
    }
  }, [isConnected, handleMessage]);

  // Unsubscribe from a list
  const unsubscribe = useCallback((listId) => {
    const listIdStr = String(listId);
    const subscription = subscriptionsRef.current.get(listIdStr);

    if (subscription) {
      try {
        subscription.unsubscribe();
      } catch (err) {
        logger.error('Error unsubscribing:', err);
      }
    }

    subscriptionsRef.current.delete(listIdStr);
  }, []);

  // Connect to WebSocket
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    const sockJsUrl = getSockJsUrl();
    logger.debug('Connecting to WebSocket via SockJS:', sockJsUrl);

    const client = new Client({
      webSocketFactory: () => new SockJS(sockJsUrl),
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      debug: (str) => {
        logger.debug('STOMP:', str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        logger.debug('WebSocket STOMP connected via SockJS');
        setIsConnected(true);
        clientRef.current = client;

        // Subscribe to pending lists
        subscriptionsRef.current.forEach((subscription, listId) => {
          if (!subscription) {
            const destination = `/topic/shopping-list/${listId}`;
            const newSubscription = client.subscribe(destination, handleMessage);
            subscriptionsRef.current.set(listId, newSubscription);
            logger.debug('WebSocket subscribed to:', destination);
          }
        });
      },
      onDisconnect: () => {
        logger.debug('WebSocket STOMP disconnected');
        setIsConnected(false);
      },
      onStompError: (frame) => {
        // Only log as error if online, otherwise it's expected
        if (navigator.onLine) {
          logger.error('STOMP error:', frame.headers['message'], frame.body);
        } else {
          logger.debug('STOMP connection failed (offline mode)');
        }
      },
      onWebSocketError: (event) => {
        // Only log as error if online, otherwise it's expected behavior
        if (navigator.onLine) {
          logger.error('WebSocket error:', event);
        } else {
          logger.debug('WebSocket connection failed (offline mode - expected)');
        }
      }
    });

    client.activate();
    clientRef.current = client;

    return () => {
      if (client.active) {
        client.deactivate();
      }
      setIsConnected(false);
    };
  }, [user, handleMessage]);

  return {
    isConnected,
    subscribe,
    unsubscribe,
    reconnect: () => clientRef.current?.activate()
  };
};

export default useShoppingListWebSocket;
